import {useState} from 'react'
import {Button, Form, FormInstance, InputNumber, Switch, TimePicker} from 'antd'
import Icon from '@mdi/react'
import {mdiTrashCanOutline} from '@mdi/js'
import {SecondaryTherapyDataSchemaElement} from 'src/constants/secondaryTherapyFields'
import {
  CALCULATION_CRITERION_UNIT_OPTIONS,
  DEFAULT_DOSAGE_UNIT_TYPE,
  DOSAGE_UNIT_OPTIONS,
  INFUSION_RATE_UNIT_OPTIONS,
  VIAL_DOSAGE_UNIT_OPTIONS
} from './therapyDialogsConstants'
import {TwoColumnsGrid} from './TherapyGridStyled'
import UnitSelector from './UnitSelector'

const getContainer = () => document.body

type DosageFieldsProps = {
  form: FormInstance
  dataSchema: SecondaryTherapyDataSchemaElement[]
}

const DosageFields: React.FC<DosageFieldsProps> = ({form, dataSchema}) => {
  const [isCustomDosagesEnabled, setIsCustomDosagesEnabled] = useState<boolean>(Boolean(form.getFieldValue('customDosages')?.length))
  const isFieldVisible = (key: string): boolean => dataSchema.some((element) => element.value === key)
  const fieldLabel = (key: string, fallback: string): string => dataSchema.find((element) => element.value === key)?.name ?? fallback

  const onCustomDosagesToggle = (checked: boolean) => {
    setIsCustomDosagesEnabled(checked)
    form.setFieldValue('customDosages', checked ? [{dosage: {unitType: DEFAULT_DOSAGE_UNIT_TYPE}}] : undefined)
  }

  return (
    <div>
      <TwoColumnsGrid>
        {isFieldVisible('assumptionDosage') && (
          <Form.Item label={fieldLabel('assumptionDosage', 'Dosaggio per assunzione')} name='assumptionDosage'>
            <UnitSelector unitTypeOptions={DOSAGE_UNIT_OPTIONS} />
          </Form.Item>
        )}
        {isFieldVisible('assumptionDosage.infusionRate') && (
          <Form.Item label={fieldLabel('assumptionDosage.infusionRate', 'Velocità infusionale')} name='infusionRate'>
            <UnitSelector unitTypeOptions={INFUSION_RATE_UNIT_OPTIONS} />
          </Form.Item>
        )}
        {isFieldVisible('assumptionDosage.secondaryDosage') && (
          <Form.Item label={fieldLabel('assumptionDosage.secondaryDosage', 'Dosaggio secondario')} name='secondaryDosage'>
            <UnitSelector unitTypeOptions={DOSAGE_UNIT_OPTIONS} />
          </Form.Item>
        )}
        {isFieldVisible('assumptionDosage.dilution') && (
          <Form.Item label={fieldLabel('assumptionDosage.dilution', 'Diluizione (ml)')} name='dilution'>
            <InputNumber min={0} style={{width: '100%'}} />
          </Form.Item>
        )}
        {isFieldVisible('drugDosage') && (
          <Form.Item label={fieldLabel('drugDosage', 'Dosaggio singolo')} name='drugDosage'>
            <UnitSelector unitTypeOptions={DOSAGE_UNIT_OPTIONS} />
          </Form.Item>
        )}
        {isFieldVisible('dailyDosage') && (
          <Form.Item label={fieldLabel('dailyDosage', 'Dosaggio giornaliero')} name='dailyDosage'>
            <UnitSelector unitTypeOptions={DOSAGE_UNIT_OPTIONS} />
          </Form.Item>
        )}
        {isFieldVisible('weeklyDosage') && (
          <Form.Item label={fieldLabel('weeklyDosage', 'Dosaggio settimanale')} name='weeklyDosage'>
            <UnitSelector unitTypeOptions={DOSAGE_UNIT_OPTIONS} />
          </Form.Item>
        )}
        {isFieldVisible('monthlyDosage') && (
          <Form.Item label={fieldLabel('monthlyDosage', 'Dosaggio mensile')} name='monthlyDosage'>
            <UnitSelector unitTypeOptions={DOSAGE_UNIT_OPTIONS} />
          </Form.Item>
        )}
        {isFieldVisible('vialDosage') && (
          <Form.Item label={fieldLabel('vialDosage', 'Dosaggio fiala')} name='vialDosage'>
            <UnitSelector unitTypeOptions={VIAL_DOSAGE_UNIT_OPTIONS} />
          </Form.Item>
        )}
        {isFieldVisible('calculationCriterion') && (
          <Form.Item label={fieldLabel('calculationCriterion', 'Criterio di calcolo')} name='calculationCriterion'>
            <UnitSelector unitTypeOptions={CALCULATION_CRITERION_UNIT_OPTIONS} />
          </Form.Item>
        )}
      </TwoColumnsGrid>
      <Form.Item label='Personalizza dosaggi'>
        <Switch checked={isCustomDosagesEnabled} onChange={onCustomDosagesToggle} />
      </Form.Item>
      {isCustomDosagesEnabled && (
        <Form.List name='customDosages'>
          {(fields, {add, remove}) => (
            <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
              {fields.map((field, index) => (
                <div key={field.key} style={{border: '1px solid #d9d9d9', borderRadius: '16px', padding: '16px'}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px'}}>
                    <b>{`Dosaggio ${index + 1}`}</b>
                    {index !== 0 && (
                      <Button
                        danger
                        icon={<Icon path={mdiTrashCanOutline} size={0.7} />}
                        onClick={() => remove(field.name)}
                        size='small'
                      />
                    )}
                  </div>
                  <TwoColumnsGrid>
                    <Form.Item label='Valore' name={[field.name, 'dosage']}>
                      <UnitSelector unitTypeOptions={DOSAGE_UNIT_OPTIONS} />
                    </Form.Item>
                    <Form.Item label='Velocità infusionale' name={[field.name, 'infusionRate']}>
                      <UnitSelector unitTypeOptions={INFUSION_RATE_UNIT_OPTIONS} />
                    </Form.Item>
                    <Form.Item label='Dosaggio secondario' name={[field.name, 'secondaryDosage']}>
                      <UnitSelector unitTypeOptions={DOSAGE_UNIT_OPTIONS} />
                    </Form.Item>
                    <Form.Item label='Diluizione (ml)' name={[field.name, 'dilution']}>
                      <InputNumber min={0} style={{width: '100%'}} />
                    </Form.Item>
                    <Form.Item label='Orario di somministrazione' name={[field.name, 'administrationHour']}>
                      <TimePicker format='HH:mm' getPopupContainer={getContainer} popupStyle={{zIndex: 1400}} style={{width: '100%'}} />
                    </Form.Item>
                  </TwoColumnsGrid>
                </div>
              ))}
              <Button onClick={() => add({dosage: {unitType: DEFAULT_DOSAGE_UNIT_TYPE}})} type='dashed'>
                {'Aggiungi dosaggio'}
              </Button>
            </div>
          )}
        </Form.List>
      )}
    </div>
  )
}

export default DosageFields
